import { useState } from 'react'
import { useQuery } from 'react-query'
import { competitionApi } from '../services/api'
import LoadingSpinner from '../components/LoadingSpinner'
import { TrophyIcon, CalendarIcon, UserGroupIcon } from '@heroicons/react/24/outline'

interface CompetitionResult {
  id: number
  name: string
  category: string
  type: 'individual' | 'team'
  status: 'completed' | 'ongoing' | 'pending'
  winner_name?: string
  winner_score?: number
  runner_up_name?: string
  runner_up_score?: number
  completed_at?: string
}

const ResultsPage = () => {
  const [filterCategory, setFilterCategory] = useState<string>('all')
  const [filterStatus, setFilterStatus] = useState<string>('completed')

  const { data: competitions = [], isLoading, error } = useQuery(
    ['competition-results', filterStatus],
    () => competitionApi.getCompetitions(),
    {
      select: (response) => {
        const results = response.data || []
        return results.filter((comp: any) => {
          const statusMatch = filterStatus === 'all' || comp.status === filterStatus
          return statusMatch
        })
      }
    }
  )

  const categories = [
    { value: 'all', label: 'All Categories' },
    { value: 'coptic_hymns', label: 'Coptic Hymns' },
    { value: 'coptic_language', label: 'Coptic Language' },
    { value: 'spiritual', label: 'Spiritual' },
    { value: 'art', label: 'Art' },
    { value: 'sports', label: 'Sports' }
  ]

  const filteredResults = competitions.filter((comp: any) => {
    const categoryMatch = filterCategory === 'all' || comp.category === filterCategory
    return categoryMatch
  })

  if (error) {
    return (
      <div className="min-h-screen bg-gray-50 py-8">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="bg-red-50 border border-red-200 rounded-lg p-4">
            <p className="text-red-800">Error loading results: {(error as any).message}</p>
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-gray-50 py-8">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-900">Competition Results</h1>
          <p className="mt-2 text-gray-600">
            View results from completed Mahragan competitions
          </p>
        </div>

        {/* Filters */}
        <div className="bg-white rounded-lg shadow-sm p-6 mb-8">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* Category Filter */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Category
              </label>
              <select
                value={filterCategory}
                onChange={(e) => setFilterCategory(e.target.value)}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              >
                {categories.map((cat) => (
                  <option key={cat.value} value={cat.value}>
                    {cat.label}
                  </option>
                ))}
              </select>
            </div>

            {/* Status Filter */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Status
              </label>
              <select
                value={filterStatus}
                onChange={(e) => setFilterStatus(e.target.value)}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              >
                <option value="all">All Statuses</option>
                <option value="completed">Completed</option>
                <option value="ongoing">Ongoing</option>
                <option value="pending">Pending</option>
              </select>
            </div>
          </div>
        </div>

        {/* Loading State */}
        {isLoading && <LoadingSpinner />}

        {/* Results Grid */}
        {!isLoading && filteredResults.length === 0 ? (
          <div className="text-center py-12 bg-white rounded-lg shadow-sm">
            <TrophyIcon className="mx-auto h-12 w-12 text-gray-400" />
            <h3 className="mt-2 text-sm font-medium text-gray-900">No results found</h3>
            <p className="mt-1 text-sm text-gray-500">
              Try adjusting your filters or check back later for new results.
            </p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {filteredResults.map((result: CompetitionResult) => (
              <div key={result.id} className="bg-white rounded-lg shadow-sm hover:shadow-md transition-shadow p-6">
                {/* Header */}
                <div className="flex items-start justify-between mb-4">
                  <div className="flex-1">
                    <h3 className="text-lg font-semibold text-gray-900">{result.name}</h3>
                    <p className="text-sm text-gray-600 mt-1">{result.category}</p>
                  </div>
                  <span className={`inline-flex px-3 py-1 text-xs font-semibold rounded-full ${
                    result.status === 'completed' ? 'bg-green-100 text-green-800' :
                    result.status === 'ongoing' ? 'bg-blue-100 text-blue-800' :
                    'bg-yellow-100 text-yellow-800'
                  }`}>
                    {result.status}
                  </span>
                </div>

                {/* Type */}
                <div className="flex items-center text-sm text-gray-600 mb-4">
                  <UserGroupIcon className="h-4 w-4 mr-2" />
                  {result.type === 'individual' ? 'Individual' : 'Team'} Competition
                </div>

                {/* Results */}
                {result.status === 'completed' && (
                  <div className="space-y-3 border-t border-gray-200 pt-4">
                    {result.winner_name && (
                      <div>
                        <p className="text-xs text-gray-600 uppercase tracking-wide">🥇 Winner</p>
                        <p className="text-sm font-semibold text-gray-900">{result.winner_name}</p>
                        {result.winner_score && (
                          <p className="text-xs text-gray-600">Score: {result.winner_score}</p>
                        )}
                      </div>
                    )}
                    {result.runner_up_name && (
                      <div>
                        <p className="text-xs text-gray-600 uppercase tracking-wide">🥈 Runner Up</p>
                        <p className="text-sm font-semibold text-gray-900">{result.runner_up_name}</p>
                        {result.runner_up_score && (
                          <p className="text-xs text-gray-600">Score: {result.runner_up_score}</p>
                        )}
                      </div>
                    )}
                  </div>
                )}

                {/* Completed Date */}
                {result.completed_at && (
                  <div className="flex items-center text-sm text-gray-600 mt-4 pt-4 border-t border-gray-200">
                    <CalendarIcon className="h-4 w-4 mr-2" />
                    {new Date(result.completed_at).toLocaleDateString()}
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  )
}

export default ResultsPage

